/**
 * Weight conversion utilities for rich weight format integration
 * 
 * Handles conversion between frontend number format and backend rich weight objects
 * Aligns with backend micrograms-based system for consistency
 */

import isPlainObject from 'lodash/isPlainObject'

/** Canonical micrograms-per-troy-ounce factor matching the DB seed (metal_units.base_units_per_unit). */
export const MCG_PER_TOZ = 31103500

// Conversion factors to micrograms (base unit). Aligns with backend DB-driven conversion.
// TOZ and OZT are separate abbreviations with the same microgram factor.
const TO_MICROGRAMS = Object.freeze({
  'MCG': 1,                       // Micrograms (base unit)
  'G': 1000000,                   // Grams to micrograms
  'KG': 1000000000,               // Kilograms to micrograms
  'METRIC_TONNE': 1000000000000,  // Metric tonnes to micrograms
  'TOZ': MCG_PER_TOZ,
  'OZT': MCG_PER_TOZ,             // Contract troy-ounce wire token
  'OZ': 28349523.125,             // Standard ounces to micrograms
  'DWT': 1555173.84,              // Pennyweight to micrograms (1.55517384g * 1M mcg/g)
  'LB': 453592370,                // Avoirdupois pound to micrograms (453.59237g * 1M mcg/g)
})

/** Rich weight property name for each unit code */
export const RICH_WEIGHT_FIELDS = {
  'TOZ': 'troyOunces',
  'G': 'grams',
  'OZ': 'ounces',
  'DWT': 'pennyweight',
  'LB': 'pounds',
  'MCG': 'micrograms'
}

/**
 * Convert a numeric weight value to rich weight format for API submission
 * @param {number|string|null} value - Weight value
 * @param {string} unit - Weight unit ('TOZ', 'G', 'OZ', 'DWT', 'LB', 'MCG')
 * @returns {object|null} Rich weight object with single unit property
 */
export const convertToRichWeight = (value, unit = 'TOZ') => {
  if (value == null || value === '') {
    return null
  }
  
  const numericValue = Number(value)
  if (isNaN(numericValue) || numericValue === 0) {
    return null // API expects null for zero weights or invalid numbers
  }
  
  const richUnit = RICH_WEIGHT_FIELDS[unit.toUpperCase()] || 'troyOunces'
  return { [richUnit]: numericValue }
}

/**
 * Extract display weight from rich weight object for UI display
 * @param {object|number|null} richWeight - Rich weight object or legacy number
 * @param {string} preferredUnit - Preferred unit to extract (e.g., 'troyOunces', 'grams')
 * @returns {number|null} Numeric weight value for display
 */
export const extractDisplayWeight = (richWeight, preferredUnit = 'troyOunces') => {
  if (richWeight == null) {
    return null
  }
  
  // Handle legacy numeric format (backward compatibility)
  if (typeof richWeight === 'number') {
    return richWeight
  }
  
  // Handle rich weight object (but not arrays or other object types)
  if (isPlainObject(richWeight)) {
    // Only return the requested unit - no dangerous fallbacks
    if (richWeight[preferredUnit] != null) {
      const value = Number(richWeight[preferredUnit])
      return isNaN(value) ? null : value
    }
  }
  
  return null
}

/**
 * Compute fine weight from gross weight and purity (decimal 0–1).
 * Returns 0 for invalid inputs (null, undefined, NaN, grossWeight ≤ 0, purity ≤ 0 or > 1).
 * @param {number|null|undefined} grossWeight - Gross weight in same units as result
 * @param {number|null|undefined} purity - Purity as decimal (0–1)
 * @returns {number} Fine weight or 0 when invalid
 */
export const computeFineWeight = (grossWeight, purity) => {
  const g = Number(grossWeight)
  const p = Number(purity)
  if (!Number.isFinite(g) || g <= 0 || !Number.isFinite(p) || p <= 0 || p > 1) {
    return 0
  }
  return g * p
}

/**
 * Determine the appropriate weight unit for a given context
 * @param {string} context - Context hint ('bar', 'fire', 'lot')
 * @returns {string} Appropriate unit code
 */
export const getContextUnit = (context) => {
  const contextMap = {
    'fire': 'G',      // Fire assays typically in grams
    'cup': 'G',       // Cup results in grams
    'dore': 'G',      // Dore results in grams
    'bar': 'TOZ',     // Bar weights in troy ounces
    'lot': 'TOZ',     // Lot weights in troy ounces
    'pin': 'TOZ'      // Pin weights in troy ounces
  }

  return contextMap[context] || 'TOZ'
}

/**
 * Convert between weight units using micrograms as base unit (aligns with backend)
 * @param {number} value - Weight value to convert
 * @param {string} fromUnit - Source unit ('TOZ'/'OZT', 'G', 'KG', 'METRIC_TONNE', 'OZ', 'DWT', 'MCG')
 * @param {string} toUnit - Target unit ('TOZ'/'OZT', 'G', 'KG', 'METRIC_TONNE', 'OZ', 'DWT', 'MCG')
 * @returns {number|null} Converted value, or null for invalid input/units
 */
export const convertWeightUnit = (value, fromUnit, toUnit) => {
  if (value == null || isNaN(value)) return null

  const fromFactor = TO_MICROGRAMS[String(fromUnit).toUpperCase()]
  const toFactor = TO_MICROGRAMS[String(toUnit).toUpperCase()]
  if (!fromFactor || !toFactor) return null

  // Convert to micrograms first (base unit), then to the target unit
  const micrograms = value * fromFactor
  return micrograms / toFactor
}

/**
 * Convert fire assay sample weight (grams) to troy ounces for bar weight calculations
 * Useful for melt loss calculations where fire samples contribute to total loss
 * @param {number} grams - Sample weight in grams
 * @returns {number} Weight in troy ounces
 */
export const convertFireSampleToTroyOunces = (grams) => {
  return convertWeightUnit(grams, 'G', 'TOZ')
}

/**
 * Convert bar weight (troy ounces) to grams for fire assay comparisons
 * Useful for validating expected vs actual sample weights
 * @param {number} troyOunces - Weight in troy ounces
 * @returns {number} Weight in grams
 */
export const convertTroyOuncesToGrams = (troyOunces) => {
  return convertWeightUnit(troyOunces, 'TOZ', 'G')
}

/**
 * Convert troy ounces to avoirdupois pounds (presentation-only; storage stays in troy ounces)
 * @param {number} troyOunces - Weight in troy ounces
 * @returns {number|null} Weight in pounds, or null for invalid input
 */
export const convertTroyOuncesToPounds = (troyOunces) => {
  return convertWeightUnit(troyOunces, 'TOZ', 'LB')
}

/**
 * Convert any weight to micrograms (backend base unit)
 * @param {number} value - Weight value
 * @param {string} fromUnit - Source unit
 * @returns {number} Weight in micrograms
 */
export const convertToMicrograms = (value, fromUnit) => {
  return convertWeightUnit(value, fromUnit, 'MCG')
}

/**
 * Convert micrograms to any unit
 * @param {number} micrograms - Weight in micrograms
 * @param {string} toUnit - Target unit
 * @returns {number} Weight in target unit
 */
export const convertFromMicrograms = (micrograms, toUnit) => {
  return convertWeightUnit(micrograms, 'MCG', toUnit)
}